import React, { createContext, useContext, useReducer } from 'react';
import {
  ProcessingState,
  ProcessingAction,
  ProcessingContextValue,
  ProcessingType,
  StageState,
} from './types';

const initialState: ProcessingState = {
  isProcessing: false,
  processingType: 'analysis',
  stages: [],
  connectionStatus: 'disconnected',
};

function processingReducer(state: ProcessingState, action: ProcessingAction): ProcessingState {
  console.log('[ProcessingContext] Reducing action', {
    type: action.type,
    payload: 'payload' in action ? action.payload : undefined,
    currentState: state
  });

  let newState: ProcessingState;

  switch (action.type) {
    case 'START_PROCESSING':
      newState = {
        ...state,
        isProcessing: true,
        processingType: action.payload.processingType,
        stages: [],
        result: undefined,
      };
      break;

    case 'UPDATE_STAGE': {
      const { stageId, updates } = action.payload;
      const lastStageIndex = state.stages.length - 1;
      const lastStage = lastStageIndex >= 0 ? state.stages[lastStageIndex] : null;
      
      // Check if this is an update to the current stage or a new stage
      const isNewStage = !lastStage || lastStage.id !== stageId;

      if (isNewStage) {
        // Create a new stage and collapse all previous stages
        const newStage: StageState = {
          ...updates,
          id: stageId,
          processingType: state.processingType,
          messages: [],
          isExpanded: true,
        } as StageState;

        // Update previous stages: collapse them and mark in_progress ones as interrupted with 0 progress
        const updatedStages = state.stages.map(stage => ({
          ...stage,
          isExpanded: false,
          status: stage.status === 'in_progress' ? 'interrupted' : stage.status,
          progress: stage.status === 'in_progress' ? 0 : stage.progress
        }));

        newState = {
          ...state,
          stages: [...updatedStages, newStage],
          currentStage: stageId,
        };
      } else {
        // Update the last stage while preserving existing messages
        const updatedStages = [...state.stages];
        const updatedStatus = updates.status || lastStage.status;
        
        // Reset progress to 0 if stage is completed or interrupted
        const shouldResetProgress = updatedStatus === 'completed' || updatedStatus === 'interrupted';
        
        updatedStages[lastStageIndex] = {
          ...lastStage,
          ...updates,
          progress: shouldResetProgress ? 0 : (updates.progress ?? lastStage.progress),
          messages: updates.messages || lastStage.messages,
          processingType: state.processingType,
        };

        newState = {
          ...state,
          stages: updatedStages,
          currentStage: stageId,
        };
      }
      break;
    }

    case 'ADD_MESSAGE': {
      const { stageId, message } = action.payload;
      // Find the latest stage with the given ID by searching from the end
      const stageIndex = [...state.stages].reverse().findIndex(stage => stage.id === stageId);
      const actualIndex = stageIndex === -1 ? -1 : state.stages.length - 1 - stageIndex;

      if (actualIndex === -1) {
        newState = state;
        break;
      }

      const updatedStages = [...state.stages];
      updatedStages[actualIndex] = {
        ...updatedStages[actualIndex],
        messages: [...updatedStages[actualIndex].messages, message],
      };

      newState = {
        ...state,
        stages: updatedStages,
      };
      break;
    }

    case 'SET_RESULT':
      newState = {
        ...state,
        isProcessing: false,
        result: action.payload.result,
      };
      break;

    case 'SET_CONNECTION_STATUS':
      newState = {
        ...state,
        connectionStatus: action.payload.status,
      };
      break;

    case 'TOGGLE_STAGE': {
      const { stageIndex } = action.payload;
      
      if (stageIndex < 0 || stageIndex >= state.stages.length) {
        newState = state;
        break;
      }

      const updatedStages = [...state.stages];
      updatedStages[stageIndex] = {
        ...updatedStages[stageIndex],
        isExpanded: !updatedStages[stageIndex].isExpanded,
      };

      newState = {
        ...state,
        stages: updatedStages,
      };
      break;
    }

    case 'RESET_STATE':
      newState = {
        ...initialState,
        processingType: state.processingType,
      };
      break;

    default:
      newState = state;
  }

  console.log('[ProcessingContext] State updated', {
    action: action.type,
    changes: JSON.stringify(newState) !== JSON.stringify(state),
    newState
  });

  return newState;
}

const ProcessingContext = createContext<ProcessingContextValue | undefined>(undefined);

interface ProcessingProviderProps {
  children: React.ReactNode;
  initialProcessingType?: ProcessingType;
}

export function ProcessingProvider({
  children,
  initialProcessingType = 'analysis',
}: ProcessingProviderProps) {
  const [state, dispatch] = useReducer(processingReducer, {
    ...initialState,
    processingType: initialProcessingType,
  });

  return (
    <ProcessingContext.Provider value={{ state, dispatch }}>
      {children}
    </ProcessingContext.Provider>
  );
}

export function useProcessing(): ProcessingContextValue {
  const context = useContext(ProcessingContext);
  if (!context) {
    throw new Error('useProcessing must be used within a ProcessingProvider');
  }
  return context;
} 